import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { NgIf } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { TuiButtonModule, TuiSvgModule } from '@taiga-ui/core';
import { RouterLink } from '@angular/router';
import { CartActions } from '@us/web/cart/data-access';
import { Store } from '@ngrx/store';
import { OrdersApiService } from '@us/web/orders/data-access';
import { MessageService } from '@us/web/shared/feature';
import { includes } from 'lodash';
import { BusinessModel, Order, OrderState } from '@us/web/shared/data-access';
import { tap } from 'rxjs';
import { RouteUtil } from '@us/web/shared/utils';
import { NavController } from '@ionic/angular';

@Component({
    selector: 'us-order-actions',
    standalone: true,
    imports: [NgIf, TranslocoModule, TuiButtonModule, TuiSvgModule, RouterLink],
    templateUrl: './order-actions.component.html',
    styleUrls: ['./order-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderActionsComponent implements OnChanges {
    private readonly store = inject(Store);
    private readonly ordersService = inject(OrdersApiService);
    private readonly messageService = inject(MessageService);
    private readonly translocoService = inject(TranslocoService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly navController = inject(NavController);

    @Input() cart!: Order;
    @Input() isLoading = false;
    @Input() showClearCart = false;
    @Output() buttonClick = new EventEmitter<MouseEvent>();

    readonly OrderState = OrderState;

    showCallWaiter = true;
    showCallPayment = true;
    showRating = false;

    isCallWaiterLoading = false;
    isCallPaymentLoading = false;

    ngOnChanges(changes: SimpleChanges) {
        if (changes['cart']) {
            const cart: Order = changes['cart'].currentValue;
            const isCompletedOrCanceled = includes(
                [OrderState.Canceled, OrderState.Rejected, OrderState.Complete],
                cart.current_state
            );
            const isPending = cart.current_state === OrderState.Pending;
            const isPaid = cart.paid_total >= cart.grand_total;
            const requirePayment = cart.shop?.business_model === BusinessModel.PrePayment;
            const isCompleted = cart.current_state === OrderState.Complete;

            this.showCallWaiter = !isPending && !isCompletedOrCanceled && !!cart.order_table;
            this.showCallPayment = !isCompletedOrCanceled && !isPending && !isPaid && !requirePayment;
            this.showRating = isCompleted;
        }
    }

    handleClickCallWaiterBtn(event: MouseEvent) {
        if (this.cart.order_table) {
            this.isCallWaiterLoading = true;
            this.cdr.detectChanges();
            this.ordersService
                .callWaiter(this.cart.order_table)
                .pipe(
                    tap(() => {
                        this.isCallWaiterLoading = false;
                        this.cdr.detectChanges();
                    })
                )
                .subscribe({
                    next: () => {
                        this.messageService.info(this.translocoService.translate('waiter_called'));
                    },
                    error: () => {
                        this.messageService.error(this.translocoService.translate('message.error'));
                    }
                });
        } else {
            this.messageService.warning(this.translocoService.translate('please_select_a_table'));
        }
        this.buttonClick.emit(event);
    }

    handleClickCallPaymentBtn(event: MouseEvent) {
        this.store.dispatch(CartActions.pushOrderState({ new_state: OrderState.PendingPayment }));
        this.buttonClick.emit(event);
    }

    async handleClickRatingBtn(event: MouseEvent) {
        await this.navController.navigateForward(RouteUtil.getRatingRouteUrl(this.cart.order_id));
        this.buttonClick.emit(event);
    }

    async handleClickClearCartBtn(event: MouseEvent) {
        this.store.dispatch(CartActions.clearCart({}));
        this.buttonClick.emit(event);
        await this.navController.navigateRoot(RouteUtil.getHomeRouteUrl());
    }
}
